import { token } from '@atlaskit/tokens';
import {
  BodyNormal,
  breakpointValues,
  FinePrint,
  H6,
  useResponsiveBreakpoint,
} from '@atlassian/mpac-primitives';
import { styled } from '@compiled/react';
import React from 'react';
import { Link } from 'react-router-dom';

import { dataTestIds } from '../constants';

const Wrapper = styled(Link)({
  display: 'flex',
  padding: `${token('space.100')} ${token('space.200')} ${token('space.050')} ${token(
    'space.100'
  )}`,
  marginBottom: `${token('space.050', '4px')}`,
  backgroundColor: token('elevation.surface.overlay'),
  borderRadius: '3px',
  cursor: 'pointer',
  minHeight: '78px',

  '&:hover': {
    textDecoration: 'none',
    backgroundColor: token('color.background.neutral.subtle.hovered'),
  },

  '&:active': {
    textDecoration: 'none',
    backgroundColor: token('color.background.neutral.subtle.pressed'),
  },

  '&:focus-visible': {
    outline: `2px solid ${token('color.border.focused')}`,
  },
});

const ImageWrapper = styled.div({
  height: '44px',
  width: '44px',
  marginRight: token('space.200'),
});

const FinePrintText = styled(FinePrint)({
  color: token('color.text.subtlest'),
  marginTop: token('space.025'),
  fontSize: '0.75rem',
});

const BodyNormalText = styled(BodyNormal)({
  color: token('color.text.subtlest'),
  marginTop: 0,
});

type DescriptiveItemProps = {
  iconUrl?: string;
  name: string;
  description: string;
  href: string;
  closeMenu: () => void;
  id?: string;
};

export const DescriptiveItem = (props: DescriptiveItemProps) => {
  const { iconUrl, name, description, href, closeMenu } = props;
  const { value: breakpoint } = useResponsiveBreakpoint();
  const isMobile = breakpoint < breakpointValues.md;

  return (
    <Wrapper
      id={props.id}
      tabIndex={0}
      onClick={() => closeMenu()}
      to={href}
      data-testid={`${dataTestIds.EXPANDABLE_MENU__ITEM}`}
    >
      {iconUrl && (
        <ImageWrapper>
          <img src={iconUrl} alt={`${name} logo`} aria-hidden={true} />
        </ImageWrapper>
      )}
      <div>
        <H6>{name}</H6>
        {isMobile ? (
          <BodyNormalText>{description}</BodyNormalText>
        ) : (
          <FinePrintText>{description}</FinePrintText>
        )}
      </div>
    </Wrapper>
  );
};
